/**
 * Flatten layers (W2-11 batch-tools; DESIGN.md §5.8, §5.4 "Paste as new layer + Flatten" replaces RPE LAYER-04).
 *
 * The additive layers of one summed property (X, Y, rotation, opacity, scroll speed) become one curve on layer 0.
 * The breakpoints are the union of every layer's key beats (and beat 0). Between two breakpoints each layer is
 * either constant, straight (Linear; linear in seconds, as Phira interpolates) or eased; the interval is written
 *  - EXACTLY when every layer is constant or straight (the sum is straight), when one layer is eased and the others
 *    constant (its ease, a shape-preserving sub-slice when its segment is longer than the interval), or when every
 *    varying layer has the same ease over exactly this interval (the sum has that ease);
 *  - otherwise BAKED: straight pieces from bisecting the interval until the sum deviates by at most `tolerance`
 *    (property units) on 16 sub-samples per piece (pieces no shorter than 1/1024 beat).
 * Jumps survive (left and right sums at every breakpoint), constant stretches become Stays, collinear straight
 * keys are merged. On a line with position X/Y linked, both position axes are flattened together on the same beats
 * (an interval baked on one axis is baked on both). Runs on the main thread (one line, one property).
 */

import { Ease, HOLD, LINEAR } from '../ease/index';
import type { Ease as EaseValue } from '../ease/types';
import { PROP_SPECS } from '../model/props';
import type { KeyId, LineId, LineProp, TrackId } from '../model/types';
import type { TxBuilder } from '../ops/types';
import { Beats } from '../time/beat';
import type { Beat } from '../time/types';
import type { MessageRef } from '../util/types';
import {
  cleanLinks,
  commitKeys,
  createTrack,
  curveCtx,
  defaultLeadValue,
  deleteLayerBody,
  easeY,
  newRec,
  readKeys,
  requireTrack,
  tidy,
  valueAtBeat,
  type CurveCtx,
  type KeyRec,
  type TrackInfo,
} from '../commands/kit';
import { toolReason, TOOLS_MSG } from './common';
import type { FlattenParams, ToolReport } from './types';

/** Most keys one flatten may write per axis. */
export const FLATTEN_MAX_KEYS = 20000;
/** Shortest baked piece (1/FLATTEN_MIN_DIV beat). */
export const FLATTEN_MIN_DIV = 1024;
/** Sub-samples per baked piece for the deviation measurement. */
export const FLATTEN_SAMPLES = 16;

/** One layer of an axis, with its key seconds. */
interface LayerEval {
  readonly info: TrackInfo;
  readonly keys: KeyRec[];
  readonly secs: number[];
  readonly ctx: CurveCtx;
}

interface Axis {
  readonly prop: LineProp;
  readonly layers: LayerEval[];
}

function layerEval(tx: TxBuilder, track: TrackId): LayerEval {
  const info = requireTrack(tx, track);
  const keys = readKeys(tx, track);
  return { info, keys, secs: keys.map((k) => tx.tempo.secAt(k.t)), ctx: curveCtx(tx, info) };
}

function num(v: unknown): number {
  return typeof v === 'number' ? v : 0;
}

/** Value of a layer at chart second `sec` (continuous inside segments; lead-in rules before the first key). */
function layerAtSec(l: LayerEval, sec: number): number {
  const { keys, secs } = l;
  const k0 = keys[0];
  if (k0 === undefined) return 0;
  if (sec < (secs[0] as number)) {
    switch (l.ctx.leadIn) {
      case 'extrapolate': {
        const k1 = keys[1];
        if (k1 === undefined || k0.ease.kind === 'hold') return num(k0.vOut);
        const p = (sec - (secs[0] as number)) / ((secs[1] as number) - (secs[0] as number));
        return num(k0.vOut) + (num(k1.vIn) - num(k0.vOut)) * easeY(k0.ease, p, l.ctx.info.extra);
      }
      case 'default':
        return num(defaultLeadValue(l.ctx.info));
      case 'zero':
      case 'gifLoop':
        return 0;
    }
  }
  let lo = 0;
  let hi = keys.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if ((secs[mid] as number) <= sec) lo = mid;
    else hi = mid - 1;
  }
  const k = keys[lo] as KeyRec;
  const next = keys[lo + 1];
  if (next === undefined || k.ease.kind === 'hold') return num(k.vOut);
  const p = (sec - (secs[lo] as number)) / ((secs[lo + 1] as number) - (secs[lo] as number));
  return num(k.vOut) + (num(next.vIn) - num(k.vOut)) * easeY(k.ease, p, l.ctx.info.extra);
}

function sumAtSec(axis: Axis, sec: number): number {
  let s = 0;
  for (const l of axis.layers) s += layerAtSec(l, sec);
  return s;
}

/** Left (arriving) and right (leaving) sums at a breakpoint, stored form. */
function sumAt(axis: Axis, b: Beat, side: 'left' | 'right'): number {
  let s = 0;
  for (const l of axis.layers) s += num(valueAtBeat(l.keys, b, l.ctx, side));
  return tidy(s);
}

/** How one layer moves between two breakpoints. */
type Contribution =
  | { readonly kind: 'constant' }
  | { readonly kind: 'linear' }
  | {
      readonly kind: 'eased';
      readonly ease: EaseValue;
      readonly aligned: boolean;
      readonly pa: number;
      readonly pb: number;
    }
  | { readonly kind: 'curve' };

function isLinearEase(e: EaseValue): boolean {
  return e.kind === 'tween' && Ease.rpeToTweenId(e.id) === 2;
}

function contribution(l: LayerEval, a: Beat, b: Beat, sa: number, sb: number): Contribution {
  const { keys, secs } = l;
  const k0 = keys[0];
  if (k0 === undefined) return { kind: 'constant' };
  if (Beats.lt(a, k0.t)) {
    // Before the first key: only an extrapolated first segment moves.
    if (l.ctx.leadIn !== 'extrapolate') return { kind: 'constant' };
    const k1 = keys[1];
    if (k1 === undefined || k0.ease.kind === 'hold' || num(k1.vIn) === num(k0.vOut)) return { kind: 'constant' };
    return isLinearEase(k0.ease) && !l.ctx.info.extra ? { kind: 'linear' } : { kind: 'curve' };
  }
  let i = 0;
  for (let j = keys.length - 1; j >= 0; j--) {
    if (Beats.lte((keys[j] as KeyRec).t, a)) {
      i = j;
      break;
    }
  }
  const k = keys[i] as KeyRec;
  const next = keys[i + 1];
  if (next === undefined || k.ease.kind === 'hold' || num(next.vIn) === num(k.vOut)) return { kind: 'constant' };
  if (isLinearEase(k.ease) && !l.ctx.info.extra) return { kind: 'linear' };
  const s0 = secs[i] as number;
  const s1 = secs[i + 1] as number;
  const aligned = Beats.eq(k.t, a) && Beats.eq(next.t, b);
  return { kind: 'eased', ease: k.ease, aligned, pa: (sa - s0) / (s1 - s0), pb: (sb - s0) / (s1 - s0) };
}

function sameEase(x: EaseValue, y: EaseValue): boolean {
  if (x.kind !== y.kind) return false;
  if (x.kind === 'tween' && y.kind === 'tween') return x.id === y.id && x.left === y.left && x.right === y.right;
  if (x.kind === 'bezier' && y.kind === 'bezier') return x.p.every((v, i) => v === y.p[i]);
  return true;
}

/** The ease of the part [pa, pb] of a curve (shape-preserving), or null when that is not exact. */
function subEase(e: EaseValue, pa: number, pb: number): EaseValue | null {
  let cur = e;
  if (pa > 0) {
    const r = Ease.splitAt(cur, pa);
    if (r.exactness !== 'exact') return null;
    cur = r.right;
  }
  const hi = pa > 0 ? (pb - pa) / (1 - pa) : pb;
  if (hi < 1) {
    const r = Ease.splitAt(cur, hi);
    if (r.exactness !== 'exact') return null;
    cur = r.left;
  }
  return cur;
}

/** The exact ease of an interval for an axis, or null when it must be baked. */
function exactEase(axis: Axis, a: Beat, b: Beat, sa: number, sb: number): EaseValue | 'constant' | null {
  const cs = axis.layers.map((l) => contribution(l, a, b, sa, sb));
  const moving = cs.filter((c) => c.kind !== 'constant');
  if (moving.length === 0) return 'constant';
  if (moving.every((c) => c.kind === 'linear')) return LINEAR;
  if (moving.some((c) => c.kind === 'curve')) return null;
  if (moving.length === 1) {
    const c = moving[0];
    if (c?.kind !== 'eased') return null;
    return c.aligned ? c.ease : subEase(c.ease, c.pa, c.pb);
  }
  const first = moving[0];
  if (first?.kind !== 'eased' || !first.aligned) return null;
  return moving.every((c) => c.kind === 'eased' && c.aligned && sameEase(c.ease, first.ease)) ? first.ease : null;
}

interface Piece {
  readonly t: Beat;
  /** Key values per axis. */
  readonly vIn: number[];
  readonly vOut: number[];
  /** Ease per axis of the segment starting here. */
  readonly ease: EaseValue[];
}

/** Interior bake cuts of [a, b] (both axes), and the largest deviation. */
function bakeInterval(
  tx: TxBuilder,
  axes: readonly Axis[],
  a: Beat,
  b: Beat,
  startVals: readonly number[],
  endVals: readonly number[],
  tolerance: number,
  budget: { left: number },
): { cuts: Beat[]; error: number } {
  const minLen = Beats.make(0, 1, FLATTEN_MIN_DIV);
  const cuts: Beat[] = [];
  let error = 0;
  const valueAt = (x: Beat, sec: number, ai: number): number => {
    if (Beats.eq(x, a)) return startVals[ai] as number;
    if (Beats.eq(x, b)) return endVals[ai] as number;
    return sumAtSec(axes[ai] as Axis, sec);
  };
  const pieceError = (x: Beat, y: Beat): number => {
    const sx = tx.tempo.secAt(x);
    const sy = tx.tempo.secAt(y);
    let max = 0;
    axes.forEach((axis, ai) => {
      const vx = valueAt(x, sx, ai);
      const vy = valueAt(y, sy, ai);
      for (let j = 1; j < FLATTEN_SAMPLES; j++) {
        const q = j / FLATTEN_SAMPLES;
        const sec = sx + (sy - sx) * q;
        max = Math.max(max, Math.abs(sumAtSec(axis, sec) - (vx + (vy - vx) * q)));
      }
    });
    return max;
  };
  const refine = (x: Beat, y: Beat): void => {
    const err = pieceError(x, y);
    const len = Beats.sub(y, x);
    if (err > tolerance && Beats.lt(minLen, len)) {
      const mid = Beats.add(x, Beats.mulRat(len, 1, 2));
      refine(x, mid);
      cuts.push(mid);
      if (--budget.left < 0) tx.fail(toolReason(TOOLS_MSG.flattenTooMany, { max: FLATTEN_MAX_KEYS }));
      refine(mid, y);
      return;
    }
    error = Math.max(error, err);
  };
  refine(a, b);
  return { cuts, error };
}

/** Removes keys between two straight (or constant) segments that lie on one line in seconds, on every axis. */
function mergeCollinear(tx: TxBuilder, pieces: Piece[]): Piece[] {
  const out: Piece[] = [];
  for (const p of pieces) {
    out.push(p);
    while (out.length >= 3) {
      const c = out[out.length - 1] as Piece;
      const m = out[out.length - 2] as Piece;
      const a = out[out.length - 3] as Piece;
      const sa = tx.tempo.secAt(a.t);
      const sm = tx.tempo.secAt(m.t);
      const sc = tx.tempo.secAt(c.t);
      const ok = a.ease.every((e, ai) => {
        const em = m.ease[ai] as EaseValue;
        const straight = (x: EaseValue): boolean => x.kind === 'hold' || isLinearEase(x);
        if (!straight(e) || !straight(em)) return false;
        const va = a.vOut[ai] as number;
        const vmIn = m.vIn[ai] as number;
        const vmOut = m.vOut[ai] as number;
        const vc = c.vIn[ai] as number;
        if (vmIn !== vmOut) return false;
        const scale = Math.max(1, Math.abs(va), Math.abs(vc), Math.abs(vmIn));
        const expected = va + ((vc - va) * (sm - sa)) / (sc - sa);
        return Math.abs(expected - vmIn) <= 1e-9 * scale;
      });
      if (!ok) break;
      out.splice(out.length - 2, 1);
      a.ease.forEach((_, ai) => {
        const va = a.vOut[ai] as number;
        const vc = c.vIn[ai] as number;
        a.ease[ai] = va === vc ? HOLD : LINEAR;
      });
    }
  }
  return out;
}

/** Breakpoints of the axes: beat 0 and every key beat, ascending. */
function breakpoints(axes: readonly Axis[]): Beat[] {
  const all = new Map<string, Beat>([[Beats.key(Beats.ZERO), Beats.ZERO]]);
  for (const axis of axes) for (const l of axis.layers) for (const k of l.keys) all.set(Beats.key(k.t), k.t);
  return [...all.values()].sort((x, y) => Beats.cmp(x, y));
}

function flattenAxes(
  tx: TxBuilder,
  axes: readonly Axis[],
  tolerance: number,
): { pieces: Piece[]; baked: number; error: number } {
  const bps = breakpoints(axes);
  const budget = { left: FLATTEN_MAX_KEYS - bps.length };
  if (budget.left < 0) tx.fail(toolReason(TOOLS_MSG.flattenTooMany, { max: FLATTEN_MAX_KEYS }));
  const pieces: Piece[] = [];
  let baked = 0;
  let error = 0;
  for (let i = 0; i < bps.length; i++) {
    const a = bps[i] as Beat;
    const b = bps[i + 1];
    const vIn = axes.map((ax) => sumAt(ax, a, 'left'));
    const vOut = axes.map((ax) => sumAt(ax, a, 'right'));
    if (b === undefined) {
      pieces.push({ t: a, vIn, vOut, ease: axes.map(() => LINEAR) });
      break;
    }
    const sa = tx.tempo.secAt(a);
    const sb = tx.tempo.secAt(b);
    const eases = axes.map((ax) => exactEase(ax, a, b, sa, sb));
    if (eases.every((e) => e !== null)) {
      pieces.push({ t: a, vIn, vOut, ease: eases.map((e) => (e === 'constant' ? HOLD : e)) });
      continue;
    }
    const endVals = axes.map((ax) => sumAt(ax, b, 'left'));
    const r = bakeInterval(tx, axes, a, b, vOut, endVals, tolerance, budget);
    baked++;
    error = Math.max(error, r.error);
    pieces.push({ t: a, vIn, vOut, ease: axes.map(() => LINEAR) });
    for (const c of r.cuts.sort((x, y) => Beats.cmp(x, y))) {
      const sec = tx.tempo.secAt(c);
      const v = axes.map((ax) => tidy(sumAtSec(ax, sec)));
      pieces.push({ t: c, vIn: v, vOut: [...v], ease: axes.map(() => LINEAR) });
    }
  }
  return { pieces: mergeCollinear(tx, pieces), baked, error };
}

/** Axes to flatten: the property, plus the other position axis on X/Y-linked lines. */
function axesFor(tx: TxBuilder, line: LineId, prop: LineProp): { axes: Axis[]; partner: boolean } {
  const rec = tx.doc.lines.get(line);
  const props: LineProp[] =
    rec?.editor.xyLinked === true && (prop === 'moveX' || prop === 'moveY') ? ['moveX', 'moveY'] : [prop];
  const axes = props.map((pr) => ({
    prop: pr,
    layers: [...(tx.idx.tracksOfLine(line).get(pr) ?? [])].map((t) => layerEval(tx, t)),
  }));
  return { axes: axes.filter((a) => a.prop === prop || a.layers.length > 0), partner: props.length > 1 };
}

export function flattenLayers(tx: TxBuilder, p: FlattenParams): ToolReport {
  const line = tx.doc.lines.get(p.line);
  if (line === undefined) tx.fail(toolReason(TOOLS_MSG.lineMissing));
  if (!PROP_SPECS[p.prop].layered) tx.fail(toolReason(TOOLS_MSG.flattenNotLayered));
  if (!Number.isFinite(p.tolerance) || p.tolerance <= 0) tx.fail(toolReason(TOOLS_MSG.flattenTolerance));
  const { axes, partner } = axesFor(tx, p.line, p.prop);
  const main = axes.find((a) => a.prop === p.prop);
  if (main === undefined || main.layers.length < 2) tx.fail(toolReason(TOOLS_MSG.flattenOneLayer));

  const { pieces, baked, error } = flattenAxes(tx, axes, p.tolerance);
  const unlinked = new Set<number>();
  let written = 0;
  const segments: KeyId[] = [];
  axes.forEach((axis, ai) => {
    for (const l of axis.layers) for (const k of l.keys) if (k.link !== 0) unlinked.add(k.link);
    const layer0 = axis.layers.find((l) => l.info.layer === 0);
    // Keys of layer 0 at a kept beat keep their ids (stable selection).
    const idAt = new Map<string, KeyId>();
    if (layer0 !== undefined) for (const k of layer0.keys) if (k.id !== null) idAt.set(Beats.key(k.t), k.id);
    const keys: KeyRec[] = pieces.map((pc) => {
      const rec = newRec(pc.t, pc.vIn[ai] as number, pc.vOut[ai] as number, pc.ease[ai] as EaseValue);
      rec.id = idAt.get(Beats.key(pc.t)) ?? null;
      return rec;
    });
    for (const l of axis.layers) if (l.info.layer !== 0) deleteLayerBody(tx, p.line, axis.prop, l.info.layer);
    const info = layer0?.info ?? createTrack(tx, { kind: 'line', line: p.line, prop: axis.prop, layer: 0 });
    commitKeys(tx, info, keys);
    written += keys.length;
    for (const k of keys.slice(0, -1)) if (k.id !== null) segments.push(k.id);
  });
  cleanLinks(tx, unlinked);
  const notes: MessageRef[] = [];
  if (baked === 0) notes.push(toolReason(TOOLS_MSG.flattenExact));
  else notes.push(toolReason(TOOLS_MSG.flattenBaked, { count: baked, error: Number(error.toPrecision(3)) }));
  if (partner) notes.push(toolReason(TOOLS_MSG.flattenPartner));
  for (const n of notes) tx.note(n);
  tx.setSelection({ segments, keys: [], activeLine: p.line });
  tx.setFocus({ line: p.line, beat: Beats.ZERO });
  return { changed: written, maxError: error, notes };
}
